import { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { useParams } from "react-router-dom";
import { usePermissionValidator } from "../../../api/UserPermissionValidators";

import { CompanyUpdateInput, PDFTemplate } from "../../../api/Companies";
import {
  useCompanyUpdate,
  useCreateSamplePDF,
  useGetCompanyEx,
  useImageToBase64,
} from "../../../api/CompaniesApi";

import CompanyUpdateToggle from "../../companyEdit/CompanyUpdateToggle";
import { Transition } from "history";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../../../shared-components/ConfirmationDialog";
import { useBlocker } from "../../../utils/blocker";
import LoadingSpinner from "../../../shared-components/LoadingSpinner";
import Button, { ButtonColorTypes } from "../../../shared-components/Button";
import InputField from "../../../shared-components/InputField";
import DropdownComboSimple from "../../../shared-components/DropdownComboSimple";
import LogoUploadDropZone from "../../companyEdit/LogoUploadDropZone";
import RichTextEditor from "../../../shared-components/RichTextEditor";
import { countWords } from "../../../reports/utils";
import { classNames } from "../../../utils";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

const defaultLocals: CompanyUpdateInput = {
  companyUUID: "",
  updates: new Map(),
};
const properties: string[] = [
  "review",
  "callback_config",
  "template_definition",
  "report_analytics_charts",
  "show_person_info_on_pdf",
];
const templateOptions = [
  { id: "default", label: "Fama Standard" },
  { id: "generic", label: "Generic" },
  { id: "custom", label: "Custom" },
];
const MAX_BODY_WORDS = 600;
const MAX_FOOTER_WORDS = 250;

type TemplateOption = {
  id: PDFTemplate["option"];
  label: string;
};

type TemplateDefinitionFormState = Omit<PDFTemplate, "option"> & {
  option?: TemplateOption | PDFTemplate["option"];
};

function sanitizeTemplateDefinitionForPayload(
  templateDefinition?: TemplateDefinitionFormState
): PDFTemplate {
  const option =
    typeof templateDefinition?.option === "string"
      ? templateDefinition.option
      : templateDefinition?.option?.id ?? "";

  if (option !== "custom") {
    return {
      option,
      logo_url: "",
      body: "",
      footer: "",
    };
  }

  return {
    option,
    logo_url: templateDefinition?.logo_url ?? "",
    body: templateDefinition?.body ?? "",
    footer: templateDefinition?.footer ?? "",
  };
}

const ReportSettings = () => {
  const { companyId, subid } = useParams();
  const activeId: string = !!subid ? subid : companyId ?? "";
  const { companies, subcompanies } = usePermissionValidator();
  const [readOnly, setReadOnly] = useState<boolean>(true);
  const [locals, setLocals] = useState<CompanyUpdateInput>(defaultLocals);
  const [files, setFiles] = useState<File[] | undefined>(undefined);
  const [dirty, setDirty] = useState<boolean>(false);
  const [transition, setTransition] = useState<Transition | undefined>(
    undefined
  );

  const {
    isLoading: loadingCompanyEx,
    data: companyEx,
    mutate: loadCompanyEx,
  } = useGetCompanyEx();
  const {
    isLoading: loadingSave,
    mutateAsync: companySave,
    error: companySaveError,
  } = useCompanyUpdate();
  const { mutate: logoToBase64, data: logoUrl } = useImageToBase64();
  const { mutate: createSamplePDF, isLoading: creatingSamplePDF } =
    useCreateSamplePDF();

  useEffect(() => {
    if (!activeId) {
      return;
    }
    loadCompanyEx(activeId);
    setLocals({
      companyUUID: activeId,
      updates: new Map(),
    });
    setDirty(false);
    setFiles(undefined);
  }, [activeId, loadCompanyEx]);

  useEffect(() => {
    if (activeId === companyId) {
      setReadOnly(!companies.canEditCompanySetting);
    }
    if (activeId === subid) {
      setReadOnly(!subcompanies.canEditSubcompanySetting);
    }
  }, [companies, subcompanies, activeId, companyId, subid, setReadOnly]);

  useEffect(() => {
    if (!companyEx) {
      setLocals(defaultLocals);
      return;
    }
    const values = new Map<string, any>();
    properties.forEach((property) => {
      if (property === "template_definition") {
        let templateObj: { [key: string]: any } = {
          ...companyEx.template_definition,
        };
        templateObj["option"] = templateOptions.find(
          (option) => option.id === templateObj?.option
        );
        values.set("template_definition", templateObj);
      } else {
        values.set(property, companyEx[property as keyof typeof companyEx]);
      }
    });
    const newLocals: CompanyUpdateInput = {
      companyUUID: companyEx?.external_id,
      updates: values,
    };
    setLocals(newLocals);
  }, [companyEx]);

  const updateState = useCallback((key: string, value: any) => {
    setLocals((prev) => {
      const updates = new Map(prev.updates);
      if (key.includes(".")) {
        const [rootKey, nestedKey] = key.split(".");
        const nestedValue = { ...(updates.get(rootKey) || {}) };
        nestedValue[nestedKey] = value;
        updates.set(rootKey, nestedValue);
      } else {
        updates.set(key, value);
      }
      return {
        ...prev,
        updates,
      };
    });
    setDirty(true);
  }, []);

  useEffect(() => {
    if (logoUrl) {
      updateState("template_definition.logo_url", logoUrl);
    }
  }, [logoUrl, updateState]);

  async function saveChanges() {
    if (readOnly) {
      return;
    }
    let cleaned: CompanyUpdateInput = {
      companyUUID: locals.companyUUID,
      updates: new Map(),
    };
    locals.updates.forEach((value, key, obj) => {
      cleaned.updates.set(key, value);
      if (key === "template_definition") {
        const temp = sanitizeTemplateDefinitionForPayload(
          cleaned.updates.get(key) as TemplateDefinitionFormState | undefined
        );
        cleaned.updates.set(key, temp);
      }
    });
    await companySave(cleaned);
    setDirty(false);
  }

  const { hide, modal, show } = useConfirmationDialog();
  useEffect(() => {
    transition?.retry();
  }, [transition]);
  useBlocker((tx: Transition) => {
    if (!dirty) {
      setTransition(tx);
      return;
    }
    show(
      "Leave without saving changes?",
      "You have unsaved changes. If you leave now, your edits will be lost.",
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          setDirty(false);
          setTransition(tx);
          return;
        } else {
          hide();
        }
      }
    );
  });

  const template = locals.updates.get("template_definition") as
    | TemplateDefinitionFormState
    | undefined;
  const templateOption =
    typeof template?.option === "string"
      ? templateOptions.find((option) => option.id === template.option)
      : template?.option;
  const templateOptionId =
    typeof template?.option === "string"
      ? template.option
      : template?.option?.id ?? "";
  const bodyWordCount = countWords(template?.body || "");
  const footerWordCount = countWords(template?.footer || "");

  const debouncedUpdate = useMemo(
    () => debounce(updateState, 1000, { leading: false }),
    [updateState]
  );
  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);
  const isEmailInvalid = !isValidEmail(
    locals.updates.get("callback_config")?.email ?? ""
  );
  const footerText = `Footer text supports only one line of text and up to ${MAX_FOOTER_WORDS} words.`;

  return (
    <div className="p-7 bg-gray-50 max-w-[800px]">
      {modal}
      <h2 className="text-xl font-medium mb-4">Report Settings</h2>
      {loadingCompanyEx || !companyEx || !locals.updates.has(properties[0]) ? (
        <LoadingSpinner />
      ) : (
        <div>
          <div>
            <div className="py-6">
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="review"
                label="Review Completed Report"
                labelClassName="text-base font-medium mr-4"
                classNames="mb-5 w-fit"
              />
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="callback_config.email_users"
                label="Email Submitter When Report Completes&nbsp;&nbsp;"
                labelClassName="text-base font-medium mr-4"
                tooltip="This option allows the company to turn off all email notification to users who submitted report. Account manager can still receive email using the account manager email for notification settings."
                classNames="mb-5 w-fit"
              />
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="report_analytics_charts"
                label="Enable Analytics Chart on Reports"
                labelClassName="text-base font-medium mr-4"
                tooltip="This option allows the company to enable or disable the analytics chart on reports."
                classNames="mb-5 w-fit"
              />
              <CompanyUpdateToggle
                readOnly={readOnly}
                obj={locals}
                callback={updateState}
                field="show_person_info_on_pdf"
                label="Display Additional Person Info on PDF"
                labelClassName="text-base font-medium mr-4"
                tooltip="This setting enables additional person information page to be added to all report PDF."
                classNames="mb-5 w-fit"
              />
            </div>
            <div className="pb-6">
              <InputField
                readOnly={readOnly}
                name="email_admin"
                type="email"
                handleChange={(e) => {
                  updateState("callback_config.email", e.target.value);
                }}
                defaultValue={
                  locals.updates.get("callback_config")?.email ?? ""
                }
                label="Account Manager Email for Notifications"
                labelClassName="!text-lg mb-2"
                className={classNames(
                  "mb-5 w-96",
                  isEmailInvalid ? "border-red-500" : ""
                )}
                tooltip="Enter account manager email if the account manager wishes to receive an email notification of all reports completed for the company and sub companies if they do not wish to receive notification of all company’s reports leave this field blank."
              />
            </div>
            <div className="pb-6">
              {loadingSave ? (
                <LoadingSpinner />
              ) : (
                <Button
                  text="Save Changes"
                  onClick={(e) => {
                    e.preventDefault();
                    saveChanges();
                  }}
                  disabled={readOnly || isEmailInvalid}
                />
              )}
            </div>
            {companySaveError && (
              <div className="text-red-500 flex items-center gap-2 pt-2">
                <ExclamationTriangleIcon className="w-5 h-5" /> Error saving
                changes, please contact the administrator
              </div>
            )}
          </div>
          {["True", "true", true, "1", 1].includes(
            companyEx?.settings?.HAS_CUSTOM_PDF_TEMPLATE
          ) && (
            <div className="w-full">
              <hr className="m-5 py-6" />
              <div className="pb-6 w-96">
                <DropdownComboSimple
                  items={templateOptions}
                  onChange={(e) => {
                    updateState("template_definition.option", {
                      id: e.id,
                      label: e.label,
                    });
                  }}
                  label="Template Options"
                  placeHolder="Select Template"
                  name="teamplate-definition-option"
                  initial={templateOption}
                  disabled={readOnly}
                />
              </div>

              {templateOptionId === "custom" && (
                <>
                  <div className="pb-6">
                    <LogoUploadDropZone
                      files={files}
                      initialLogoURL={template?.logo_url}
                      onChange={(files) => {
                        if (!files || files.length === 0) {
                          updateState("template_definition.logo_url", "");
                          return;
                        }
                        setFiles(files);
                        logoToBase64(files[0]);
                      }}
                      isReadOnly={readOnly}
                    />
                  </div>
                  <div className="pb-6">
                    <RichTextEditor
                      name="body-text"
                      label={"Body Text"}
                      classes="w-[32rem]"
                      editorClassName="h-52"
                      placeholder="Input text here..."
                      handleChange={(text: string) =>
                        debouncedUpdate("template_definition.body", text)
                      }
                      value={template?.body}
                      disabled={readOnly}
                      toolbar_buttons={[
                        "bold",
                        "italic",
                        "underline",
                        "strikethrough",
                      ]}
                    />
                    <div className="mt-2 text-gray-400 text-xs">
                      <div>
                        Body Word Count: <span>{bodyWordCount}</span>
                      </div>
                      <div
                        className={
                          bodyWordCount > MAX_BODY_WORDS ? "text-red-500" : ""
                        }
                      >
                        Maximum of {MAX_BODY_WORDS} words allowed.
                      </div>
                    </div>
                  </div>
                  <div className="pb-6">
                    <div className="w-[32rem] mt-4 relative">
                      <RichTextEditor
                        name="footer-text"
                        label={"Footer Text"}
                        classes="w-[32rem]"
                        placeholder="Input text here..."
                        handleChange={(text: string) =>
                          debouncedUpdate("template_definition.footer", text)
                        }
                        value={template?.footer}
                        disabled={readOnly}
                        toolbar_buttons={[]}
                      />
                      <div className="mt-2 text-gray-400 text-xs">
                        <div>
                          Footer Word Count: <span>{footerWordCount}</span>
                        </div>
                        <div
                          className={
                            footerWordCount > MAX_FOOTER_WORDS
                              ? "text-red-500"
                              : ""
                          }
                        >
                          {footerText}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {templateOptionId && templateOptionId !== "default" && (
                <>
                  {!readOnly && (
                    <div className="w-[32rem]">
                      <div>
                        {creatingSamplePDF ? (
                          <LoadingSpinner />
                        ) : (
                          <Button
                            text="Generate and Download Sample PDF"
                            onClick={() => {
                              const sanitizedTemplateInfo =
                                sanitizeTemplateDefinitionForPayload(
                                  locals.updates.get("template_definition")
                                );
                              createSamplePDF({
                                company_uuid: activeId,
                                template_info: sanitizedTemplateInfo,
                              });
                            }}
                            colorType={ButtonColorTypes.outline}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportSettings;

function isValidEmail(email: string): boolean {
  if (!email) {
    return true;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
